import { useCallback, useEffect, useRef, useState } from "react"
import { useAuthStore } from "@/features/auth/store/auth.store"
import { contactsApi, type SearchResultContact, type SearchScope } from "@/features/meetings/services/contacts.api"
import { searchChain } from "../new-chat-scope"

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
/** The server's per-request cap (`searchContacts`), used when it cannot page. */
const OLD_SERVER_MAX = 200

/** One directory row, shaped for the picker list (not the wire shape). */
export interface DirectoryRow {
  id: string
  name: string
  email: string
  subtitle: string
  imageUrl?: string
  contactOrgName: string
}

function toRow(c: SearchResultContact): DirectoryRow {
  return {
    id: c.id,
    name: c.name || c.email,
    email: c.email,
    // Email first, not `contactOrgName`: two contacts sharing a display name
    // are usually indistinguishable by org (most rows read "inmeet"), but
    // never by email.
    subtitle: c.email || c.contactOrgName,
    imageUrl: c.avatarUrl ?? undefined,
    contactOrgName: c.contactOrgName,
  }
}

/**
 * Turn one page of raw search results into rows safe to show, and append them
 * onto what's already loaded.
 *
 * Drops a row with no id, a non-uuid id (mock/legacy data), the caller's own
 * id, or an id in `excludeIds` (already picked elsewhere, e.g. a group's
 * current members). Also drops anything whose id already appears in
 * `existing`, so a page boundary that happens to land mid-duplicate (a
 * contact edited between two fetches and shifted a row) never produces two
 * entries for one id.
 *
 * Exported standalone so this merge logic is testable without rendering the
 * hook that calls it.
 */
export function mergeDirectoryPage(
  existing: DirectoryRow[],
  page: SearchResultContact[],
  selfId: string | null,
  excludeIds?: ReadonlySet<string>,
): DirectoryRow[] {
  const seen = new Set(existing.map((r) => r.id))
  const merged = existing.slice()
  for (const c of page) {
    if (!c.id || !UUID_RE.test(c.id)) continue
    if (selfId && c.id === selfId) continue
    if (excludeIds?.has(c.id)) continue
    if (seen.has(c.id)) continue
    seen.add(c.id)
    merged.push(toRow(c))
  }
  return merged
}

/**
 * Whether a response for `requestGeneration` is still the one on screen.
 * Shared by the first-page search effect and `loadMore` so both guard state
 * updates the same way: a response for a scope, query or `enabled` state the
 * caller already left behind must never land.
 */
export function isCurrentGeneration(requestGeneration: number, currentGeneration: number): boolean {
  return requestGeneration === currentGeneration
}

/**
 * The debounced query to use right after `enabled` or `query` changes.
 * Closing the picker must win immediately rather than wait out the 200ms
 * timer: a reopen can land before that timer fires, and the first fetch
 * after reopening needs the current query, not whatever was pending before.
 * Returns `null` while a live, enabled picker should keep debouncing a typed
 * query as usual.
 */
export function nextDebouncedQuery(enabled: boolean, query: string): string | null {
  return enabled ? null : query
}

export interface UseDirectorySearchOptions {
  /** Fetch only while true, matching PresencePage's lazy-load-on-open pattern. */
  enabled: boolean
  scope: SearchScope
  query: string
  /** Rows per page, also the offset step for `loadMore`. */
  pageSize?: number
  /** Ids to leave out of the results entirely (already selected elsewhere). */
  excludeIds?: ReadonlySet<string>
}

export interface UseDirectorySearchResult {
  contacts: DirectoryRow[]
  loading: boolean
  loadingMore: boolean
  hasMore: boolean
  loadMore: () => void
  /** The scope the current `contacts` actually came from, once a typed query
   *  has widened past `scope` (narrowest tab had no matches). */
  resultScope: SearchScope | null
  error: string | null
}

/**
 * Backs every directory/contact picker: debounces the typed query, widens a
 * real search outward across scopes (`searchChain`) while leaving a browse
 * (empty query) on the selected tab alone, and pages the chosen scope's
 * results via `loadMore`.
 *
 * Stale responses are ignored with a generation counter: a scope/query change
 * bumps it, so a slow request from the scope/query the caller already moved
 * away from can never overwrite what's on screen.
 */
export function useDirectorySearch({
  enabled,
  scope,
  query,
  pageSize = 50,
  excludeIds,
}: UseDirectorySearchOptions): UseDirectorySearchResult {
  const myUserId = useAuthStore((s) => s.presenceId)
  const [contacts, setContacts] = useState<DirectoryRow[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [resultScope, setResultScope] = useState<SearchScope | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [debouncedQuery, setDebouncedQuery] = useState(query)
  useEffect(() => {
    const immediate = nextDebouncedQuery(enabled, query)
    if (immediate !== null) {
      setDebouncedQuery(immediate)
      return
    }
    const timer = setTimeout(() => setDebouncedQuery(query), 200)
    return () => clearTimeout(timer)
  }, [enabled, query])

  const generationRef = useRef(0)
  // Rows the SERVER has returned for the current list, before any filtering.
  // The next page's offset must come from this, not from `contacts.length`:
  // filtered rows (self, `excludeIds`) would make the offset lag, and a page
  // filtered out entirely would ask for that same page again forever.
  const fetchedRef = useRef(0)

  // First page: a new scope or (debounced) query always starts over, widening
  // outward on a real search and stopping at the first scope with a match.
  useEffect(() => {
    if (!enabled) {
      // Bump the generation even though nothing is fetched: it invalidates
      // whatever was still in flight from before disabling, so a slow
      // response from the last-open picker can't land after this reset.
      generationRef.current++
      setContacts([])
      setResultScope(null)
      setHasMore(false)
      // A `loadMore` left running for the picker that just closed would
      // otherwise never clear this: its own `finally` already treats this
      // generation as stale and skips touching state.
      setLoadingMore(false)
      return
    }
    const generation = ++generationRef.current
    setLoading(true)
    setError(null)
    // Same reasoning as the `!enabled` reset above: a page in flight for the
    // scope/query being left behind must not keep the spinner on forever.
    setLoadingMore(false)

    const q = debouncedQuery.trim()
    const chain = searchChain(scope, q)

    void (async () => {
      for (const s of chain) {
        if (!isCurrentGeneration(generation, generationRef.current)) return
        try {
          let res = await contactsApi.search(q, s, pageSize, 0)
          if (!isCurrentGeneration(generation, generationRef.current)) return
          // An older server sends no `hasMore` and cannot page, so a full first
          // page there means the list was cut short: ask once for its maximum.
          if (res.hasMore === undefined && res.contacts.length >= pageSize) {
            res = await contactsApi.search(q, s, OLD_SERVER_MAX, 0)
            if (!isCurrentGeneration(generation, generationRef.current)) return
          }
          fetchedRef.current = res.contacts.length
          const rows = mergeDirectoryPage([], res.contacts, myUserId, excludeIds)
          const isLast = s === chain[chain.length - 1]
          if (rows.length > 0 || isLast) {
            setContacts(rows)
            setResultScope(rows.length > 0 ? s : null)
            // Missing `hasMore` (older server) means there is no second page.
            setHasMore(res.hasMore ?? false)
            return
          }
        } catch (err) {
          if (!isCurrentGeneration(generation, generationRef.current)) return
          setContacts([])
          setResultScope(null)
          setHasMore(false)
          setError(err instanceof Error ? err.message : 'Search failed')
          return
        }
      }
    })().finally(() => {
      if (isCurrentGeneration(generation, generationRef.current)) setLoading(false)
    })
  }, [enabled, scope, debouncedQuery, pageSize, myUserId, excludeIds])

  const loadMore = useCallback(() => {
    if (!enabled || !hasMore || loading || loadingMore || !resultScope) return
    const generation = generationRef.current
    // Same scope the current rows came from, not the selected tab: a loaded
    // page must keep asking the scope it actually widened to.
    const offset = fetchedRef.current
    setLoadingMore(true)
    void contactsApi
      .search(debouncedQuery.trim(), resultScope, pageSize, offset)
      .then((res) => {
        if (!isCurrentGeneration(generation, generationRef.current)) return
        fetchedRef.current += res.contacts.length
        setContacts((prev) => mergeDirectoryPage(prev, res.contacts, myUserId, excludeIds))
        // An empty page ends the list even if the server claimed more, so the
        // sentinel can never keep re-requesting the same offset.
        setHasMore(res.contacts.length > 0 && (res.hasMore ?? false))
      })
      .catch((err) => {
        if (!isCurrentGeneration(generation, generationRef.current)) return
        setError(err instanceof Error ? err.message : 'Search failed')
      })
      .finally(() => {
        // A newer search already reset this the moment it started (see
        // above), so this only fires for a response that is still current.
        if (isCurrentGeneration(generation, generationRef.current)) setLoadingMore(false)
      })
  }, [enabled, hasMore, loading, loadingMore, resultScope, debouncedQuery, pageSize, myUserId, excludeIds])

  return { contacts, loading, loadingMore, hasMore, loadMore, resultScope, error }
}
